import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";
import { normalizeJwtToken } from "../../utils/normalizeJwtToken";
import { resolveCompanyTimezone } from "../../utils/companyTimezone";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  const persistCompanyTimezone = (payloadUser) => {
    const timezone = resolveCompanyTimezone(
      payloadUser?.company?.timezone || payloadUser?.timezone
    );
    localStorage.setItem("companyTimezone", timezone);
  };

  // Interceptors do API
  api.interceptors.request.use(
    (config) => {
      const rawToken = localStorage.getItem("token");
      if (rawToken) {
        // ✅ CORREÇÃO: Normalizar token ao ler (compatibilidade com instalações antigas)
        const normalizedToken = normalizeJwtToken(rawToken);
        if (normalizedToken) {
          config.headers["Authorization"] = `Bearer ${normalizedToken}`;
          setIsAuth(true);
          
          // ✅ ETAPA 1: Log de diagnóstico (somente se ?diag=1 na URL)
          if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('diag') === '1') {
            try {
              // Tentar decodificar JWT (sem verificar assinatura)
              const parts = normalizedToken.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                console.debug({
                  event: "auth.token.present",
                  tokenLen: normalizedToken.length,
                  exp: payload.exp,
                  iat: payload.iat,
                  alg: "HS256",
                  companyId: payload.companyId,
                  userId: payload.id
                }, `[AUTH] Token present: len=${normalizedToken.length}, exp=${payload.exp}, iat=${payload.iat}`);
              }
            } catch (e) {
              // Ignorar erro de decode
            }
          }
        }
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        const { data } = await api.post("/auth/refresh_token");
        if (data) {
          // ✅ CORREÇÃO: Salvar token puro (sem JSON.stringify)
          const normalizedToken = normalizeJwtToken(data.token);
          if (normalizedToken) {
            localStorage.setItem("token", normalizedToken);
            api.defaults.headers.Authorization = `Bearer ${normalizedToken}`;
          }
        }
        return api(originalRequest);
      }
      if (error?.response?.status === 401) {
        // ✅ ETAPA 1: Limpar storage e forçar login se token inválido/expirado
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
        
        // ✅ ETAPA 1: Log de diagnóstico
        if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('diag') === '1') {
          console.debug({
            event: "auth.token.cleared",
            reason: "401_response",
            path: error?.config?.url || "unknown"
          }, `[AUTH] Token cleared due to 401 response`);
        }
      }
      return Promise.reject(error);
    }
  );

  // Effect para inicialização do token
  useEffect(() => {
    const rawToken = localStorage.getItem("token");
    (async () => {
      if (rawToken) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          // ✅ CORREÇÃO: Normalizar token antes de usar
          const normalizedToken = normalizeJwtToken(data.token);
          if (normalizedToken) {
            // ✅ CORREÇÃO: Salvar token puro (sem JSON.stringify)
            localStorage.setItem("token", normalizedToken);
            api.defaults.headers.Authorization = `Bearer ${normalizedToken}`;
            setIsAuth(true);
            setUser(data.user || data);
            persistCompanyTimezone(data.user || data);
          }
    } catch (err) {
      console.log("Erro ao buscar usuário atual:", err);
    }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      console.log("Configurando socket para user", user.id, "company", user.companyId);
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(data.user);
            }
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
            console.log(`Listener adicionado para: ${eventName}`);
          } else {
            console.error("Socket instance não tem método 'on'", socketInstance);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
        persistCompanyTimezone(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      // ✅ CORREÇÃO: Normalizar token antes de salvar
      const normalizedToken = normalizeJwtToken(data.token);
      if (!normalizedToken) {
        throw new Error("Token inválido recebido do servidor");
      }

      if (before === true) {
        // ✅ CORREÇÃO: Salvar token puro (sem JSON.stringify)
        localStorage.setItem("token", normalizedToken);
        localStorage.setItem("companyDueDate", vencimento);
        persistCompanyTimezone(data.user || data);
        api.defaults.headers.Authorization = `Bearer ${normalizedToken}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${normalizedToken}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("companyTimezone");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
